if (!customElements.get('outin-accessory-offer')) {
  // ProductForm and UpCart publish different success events. Both consume the
  // same frozen selection; neither a click nor a cart refresh confirms an add.
  customElements.define('outin-accessory-offer', class OutinAccessoryOffer extends HTMLElement {
    constructor() {
      super();
      this.pendingSubmissions = [];
      this.addQueue = Promise.resolve();
      this.onReady = () => this.init();
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.listeners?.abort();
      this.listeners = null;
      this.awaitingFormData = false;
      this.pendingSubmissions = [];
      // Do not abort an accessory POST after the main product has succeeded.
    }

    init() {
      if (this.listeners || !this.isConnected) return;
      this.form = document.getElementById(this.getAttribute('form'));
      if (!this.form) return;
      this.status = this.querySelector('[data-accessory-status]');
      this.error = this.querySelector('[data-accessory-error]');
      this.rootUrl = window.Shopify?.routes?.root || '/';
      this.listeners = new AbortController();
      this.variantPickers = new WeakMap();
      const options = { signal: this.listeners.signal };

      this.addEventListener('change', event => {
        if (event.target.matches('[data-accessory-variant-select]')) {
          this.updateVariant(event.target);
        } else if (event.target.matches('input[type="radio"]') && event.target.closest('[data-accessory-option-position]')) {
          this.selectVariant(event.target);
        }
        this.updateMainProperties();
      }, options);
      this.querySelectorAll('[data-accessory-variant-select]').forEach(select => this.updateVariant(select));
      this.updateMainProperties();

      // UpCart stops propagation on the form/button in capture phase. Observe
      // intent at window first, then snapshot its actual FormData on the form.
      window.addEventListener('submit', event => {
        if (event.target === this.form) this.prepareSubmission();
      }, { ...options, capture: true });
      window.addEventListener('click', event => {
        const button = event.target.closest?.('button[type="submit"], button[data-seel-proxy="1"]');
        if (!button || button.disabled) return;
        if (button.closest('buy-buttons')?.getAttribute('form') === this.getAttribute('form')) this.prepareSubmission();
      }, { ...options, capture: true });
      this.form.addEventListener('formdata', event => this.captureSubmission(event), options);
      this.form.addEventListener('variant:add', event => this.onMainAdded(event), options);
      window.addEventListener('aftersell-upcart:public-events:added-to-cart', event => this.onUpcartAdded(event), options);
      window.addEventListener('aftersell-upcart-debug', ({ detail }) => {
        // UpCart reports failed form requests here, not through cart:error.
        if (detail?.level !== 'ERROR' || !detail.formData) return;
        this.pendingSubmissions = this.pendingSubmissions.filter(submission => submission.formData !== detail.formData);
      }, options);
      this.form.addEventListener('cart:error', () => {
        this.awaitingFormData = false;
        this.pendingSubmissions.shift();
      }, options);
    }

    prepareSubmission() {
      this.awaitingFormData = this.form.checkValidity();
      this.updateMainProperties();
    }

    updateMainProperties() {
      const selected = this.querySelectorAll('[data-accessory-checkbox]:checked:not(:disabled)').length > 0;
      this.querySelectorAll('[data-offer-main-property]').forEach(input => { input.disabled = !selected; });
    }

    getVariantPicker(select) {
      if (this.variantPickers.has(select)) return this.variantPickers.get(select);
      const item = select.closest('[data-accessory-item]');
      const groups = Array.from(item.querySelectorAll('[data-accessory-option-position]'), fieldset => ({
        index: Number(fieldset.dataset.accessoryOptionPosition) - 1,
        inputs: Array.from(fieldset.querySelectorAll('input[type="radio"]')),
        value: fieldset.querySelector('[data-accessory-option-value]')
      }));
      const variants = Array.from(select.options || [], option => {
        let values;
        try { values = JSON.parse(option.dataset.options || '[]'); } catch { values = []; }
        return { option, values: Array.isArray(values) ? values : [] };
      });
      const picker = { groups, variants };
      this.variantPickers.set(select, picker);
      return picker;
    }

    selectVariant(input) {
      if (!input.checked) return;
      const select = input.closest('[data-accessory-item]')?.querySelector('[data-accessory-variant-select]');
      if (!select) return;
      const { groups, variants } = this.getVariantPicker(select);
      const changed = groups.find(group => group.inputs.includes(input));
      if (!changed) return;
      const choices = groups.map(group => group.inputs.find(radio => radio.checked)?.value);
      let match;
      let bestScore = -1;
      for (const variant of variants) {
        if (variant.option.dataset.available !== 'true' || variant.values[changed.index] !== input.value) continue;
        // Keep the changed choice and preserve as many other choices as possible.
        // Never resolve a sold-out, subscription-only or nonexistent combination.
        const score = groups.reduce((total, group, index) => total + Number(variant.values[group.index] === choices[index]), 0);
        if (score > bestScore) {
          match = variant;
          bestScore = score;
        }
      }
      if (match) select.value = match.option.value;
      this.updateVariant(select);
    }

    syncVariantOptions(select) {
      const { groups, variants } = this.getVariantPicker(select);
      const variant = variants.find(entry => entry.option === select.selectedOptions[0]);
      if (!variant) return;
      groups.forEach(group => {
        const value = variant.values[group.index];
        group.inputs.forEach(input => { input.checked = input.value === value; });
        if (group.value) group.value.textContent = value || '';
      });
    }

    updateVariant(select) {
      const option = select.selectedOptions[0];
      const item = select.closest('[data-accessory-item]');
      const checkbox = item?.querySelector('[data-accessory-checkbox]');
      if (!option || !item || !checkbox) return;

      const available = option.dataset.available === 'true';
      checkbox.value = option.value;
      checkbox.disabled = !available;
      if (!available) checkbox.checked = false;
      item.dataset.unavailable = String(!available);
      const originalPrice = item.querySelector('[data-original-price]');
      const discountedPrice = item.querySelector('[data-discounted-price]');
      const soldOut = item.querySelector('[data-sold-out]');
      if (originalPrice && originalPrice.textContent.trim() !== option.dataset.originalPrice) {
        originalPrice.textContent = option.dataset.originalPrice || '';
      }
      if (discountedPrice && discountedPrice.textContent.trim() !== option.dataset.discountedPrice) {
        discountedPrice.textContent = option.dataset.discountedPrice || '';
      }
      if (soldOut) {
        soldOut.hidden = available;
        // Referenced hidden text can still be read as an accessible description.
        if (available) checkbox.removeAttribute('aria-describedby');
        else checkbox.setAttribute('aria-describedby', soldOut.id);
      }
      this.syncVariantOptions(select);
    }

    captureSubmission({ formData }) {
      if (!this.awaitingFormData) return;
      this.awaitingFormData = false;
      const variantId = Number(formData.get('id'));
      if (!Number.isSafeInteger(variantId) || variantId <= 0) return;
      const requestedQuantity = Number(formData.get('quantity') || 1);
      const quantity = Number.isSafeInteger(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : 1;
      const ids = new Set(Array.from(this.querySelectorAll('[data-accessory-checkbox]:checked:not(:disabled)'))
        .map(input => Number(input.value))
        .filter(id => Number.isSafeInteger(id) && id > 0));
      const offerId = this.dataset.offerId || '';
      const items = Array.from(ids, id => ({
        id,
        quantity,
        properties: { '_outin_offer_id': offerId, '_outin_role': 'accessory' }
      }));

      if (items.length === 0) return;
      // UpCart preserves these properties in its confirmed added-to-cart event.
      formData.set('properties[_outin_offer_id]', offerId);
      formData.set('properties[_outin_role]', 'main');
      // Freeze choices now: changes made during the main request apply next time.
      this.pendingSubmissions.push({ variantId, items, formData });
    }

    onMainAdded(event) {
      if (event.target !== this.form) return;
      this.completeSubmission((event.detail?.items || []).map(item => Number(item.variant_id || item.id)));
    }

    onUpcartAdded({ detail }) {
      const item = detail?.item;
      // This global event can also describe protection, upsells or another form.
      if (item?.properties?._outin_role !== 'main' || item.properties._outin_offer_id !== this.dataset.offerId) return;
      const quantity = Number(item.quantityAdded);
      if (!Number.isSafeInteger(quantity) || quantity <= 0) return;
      this.completeSubmission([Number(item.variantId)], quantity);
    }

    completeSubmission(variantIds, quantity) {
      const addedIds = new Set(variantIds);
      const index = this.pendingSubmissions.findIndex(submission => addedIds.has(submission.variantId));
      if (index === -1) return;
      // Consume before the async work so a second notification cannot add twice.
      const [submission] = this.pendingSubmissions.splice(index, 1);
      if (quantity !== undefined) submission.items.forEach(item => { item.quantity = quantity; });
      this.addQueue = this.addQueue.then(() => this.addAccessories(submission));
    }

    async request(url, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Cart request failed');
        return result;
      } finally {
        clearTimeout(timeout);
      }
    }

    async addAccessories(submission) {
      this.setAttribute('aria-busy', 'true');
      this.setStatus('');
      try {
        let result;
        try {
          const payload = { items: submission.items };
          if (typeof window.upcartRefreshCart !== 'function') {
            payload.sections = ['cart-drawer'];
            payload.sections_url = window.location.pathname;
          }
          result = await this.request(`${this.rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload)
          });
        } catch (error) {
          // Never retry automatically: a network failure may follow a successful POST.
          this.setStatus(this.dataset.errorMessage, true);
          console.warn('Unable to confirm accessory add:', error);
          return;
        }

        try {
          await this.syncCart(result);
          this.setStatus(this.dataset.successMessage);
        } catch (error) {
          // A refresh failure must not be reported as an add failure or retried.
          this.setStatus(this.dataset.errorMessage, true);
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          console.warn('Accessories added, but cart refresh failed:', error);
        }
        this.dispatchEvent(new CustomEvent('outin:accessory-added', { bubbles: true, detail: result }));
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    async syncCart(addResponse) {
      if (typeof window.upcartRefreshCart === 'function') {
        try {
          await this.refreshUpcart();
          return;
        } catch (error) {
          console.warn('Unable to refresh UpCart; falling back to the theme cart:', error);
        }
      }

      const cart = await this.request(`${this.rootUrl}cart.js`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      cart.sections = addResponse.sections || {};
      if (!cart.sections['cart-drawer']) {
        cart.sections = await this.request(`${this.rootUrl}?sections=cart-drawer`);
      }
      if (!cart.sections['cart-drawer']) throw new Error('Cart drawer section is unavailable');
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart }
      }));
    }

    async refreshUpcart() {
      let timeout;
      try {
        await Promise.race([
          Promise.resolve().then(() => window.upcartRefreshCart()),
          new Promise((_, reject) => {
            timeout = setTimeout(() => reject(new Error('UpCart refresh timed out')), 3000);
          })
        ]);
      } finally {
        clearTimeout(timeout);
      }
    }

    setStatus(message, isError = false) {
      if (this.status) this.status.textContent = message || '';
      if (this.error) this.error.textContent = isError ? message || '' : '';
    }
  });
}
